/**
 * Patient - Grafo completo de un paciente
 * Gestiona la colección de ClinicalNodes y sus relaciones
 */

import * as THREE from 'three';
import { ClinicalNode } from './ClinicalNode';
import {
  PatientGraph,
  ClinicalNodeData,
  ClinicalRelationship,
  TopologyMode,
  AnatomicalSystem,
  NodeType,
  TriagePriority
} from '../types/medical';
import state from '../core/state';
import { MEDICAL_PHYSICS } from '../core/config';

// ═══════════════════════════════════════════════════════════════════════════════
// CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export class Patient extends THREE.Group {
  // Datos del paciente
  data: PatientGraph;
  
  // Nodos 3D
  nodes: Map<string, ClinicalNode> = new Map();
  
  // Conexiones
  edges: EdgeConnection[] = [];
  edgeLines: THREE.LineSegments | null = null;
  
  // Sistema anatómico actual (para modo anatómico)
  currentSystem: AnatomicalSystem | null = null;
  
  // Estado
  isLoaded: boolean = false;
  private lastTopologyMode: TopologyMode | null = null;

  constructor(patientData: PatientGraph) {
    super();
    this.data = patientData;
    this.loadNodes();
    this.createEdges();
    this.applyInitialLayout();
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // CARGA DE NODOS
  // ═════════════════════════════════════════════════════════════════════════════
  
  private loadNodes(): void {
    this.data.nodes.forEach(nodeData => {
      const node = new ClinicalNode(nodeData);
      this.nodes.set(nodeData.id, node);
      this.add(node);
    });
    
    this.isLoaded = true;
  }

  private createEdges(): void {
    this.edges = [];
    
    // Crear conexiones basadas en relaciones
    this.data.nodes.forEach(nodeData => {
      const sourceNode = this.nodes.get(nodeData.id);
      if (!sourceNode) return;
      
      nodeData.relationships.forEach(rel => {
        const targetNode = this.nodes.get(rel.targetId);
        if (!targetNode) return;
        
        // Evitar duplicados (solo agregar si source < target o es bidireccional)
        const exists = this.edges.find(e => 
          (e.source === sourceNode && e.target === targetNode) ||
          (e.target === sourceNode && e.source === targetNode && e.bidirectional)
        );
        
        if (!exists) {
          this.edges.push({
            source: sourceNode,
            target: targetNode,
            relationship: rel,
            bidirectional: rel.bidirectional,
          });
        }
      });
    });
    
    this.updateEdgeVisualization();
  }

  /**
   * Crea la geometría de las líneas una única vez. Luego updateEdgePositions
   * se llama cada frame para que las aristas sigan a los nodos.
   */
  private updateEdgeVisualization(): void {
    if (this.edgeLines) {
      this.remove(this.edgeLines);
      this.edgeLines.geometry.dispose();
      (this.edgeLines.material as THREE.Material).dispose();
      this.edgeLines = null;
    }

    if (!state.ui.showEdges || this.edges.length === 0) return;

    const positions = new Float32Array(this.edges.length * 2 * 3);
    const colors = new Float32Array(this.edges.length * 2 * 3);

    this.edges.forEach((edge, i) => {
      const c = this.getRelationshipColor(edge.relationship.type);
      colors[i * 6 + 0] = c.r; colors[i * 6 + 1] = c.g; colors[i * 6 + 2] = c.b;
      colors[i * 6 + 3] = c.r; colors[i * 6 + 4] = c.g; colors[i * 6 + 5] = c.b;
    });

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.92,  // subida de 0.72 → relaciones más visibles
      linewidth: 2,   // nota: la mayoría de drivers GL lo clampan a 1 pero no molesta
      depthWrite: false,
    });

    this.edgeLines = new THREE.LineSegments(geometry, material);
    this.edgeLines.renderOrder = -1; // que las aristas queden detrás de los nodos
    this.add(this.edgeLines);
    this.updateEdges();
  }

  private getRelationshipColor(type: string): THREE.Color {
    const colorMap: Record<string, number> = {
      'causes': 0xff0000,
      'indicates': 0x00ff00,
      'contraindicates': 0xff00ff,
      'treats': 0x0000ff,
      'associated-with': 0xffff00,
      'temporal-sequence': 0x00ffff,
    };
    
    return new THREE.Color(colorMap[type] || 0x888888);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // LAYOUTS INICIALES
  // ═════════════════════════════════════════════════════════════════════════════
  
  /**
   * Layout inicial radial estratégico:
   *   - Diagnóstico(s) en el centro.
   *   - Cada otro nodo se ubica en un anillo cuyo radio depende de la
   *     "distancia semántica" al diagnóstico (BFS sobre relationships).
   *       · dist 1 (relación directa con dx)   → radio interior (0.85×)
   *       · dist 2 (relacionado con relacionado) → radio medio (1.15×)
   *       · dist ∞ (sin cadena al dx)          → radio exterior (1.45×)
   *   - Dentro de cada anillo los nodos se agrupan por sistema anatómico
   *     para preservar clustering clínico.
   *
   * Así los hallazgos "clave" del caso quedan más cerca del dx y los
   * contextuales (history, allergy) afuera — el grafo "cuenta el caso".
   */
  private applyInitialLayout(): void {
    const center = MEDICAL_PHYSICS.GRAVITY_CENTER;
    const baseRadius = MEDICAL_PHYSICS.IDEAL_DISTANCE * 1.2;

    const all = Array.from(this.nodes.values());
    if (all.length === 0) return;

    const diagnoses = all.filter(n => n.data.type === 'diagnosis');
    const others = all.filter(n => n.data.type !== 'diagnosis');

    diagnoses.forEach((d, i) => {
      d.position.set(center.x, center.y, center.z + i * 0.3);
    });

    if (others.length === 0) return;

    // BFS de distancia semántica desde los nodos de diagnóstico usando
    // las relationships declaradas en el caso. Si no hay relaciones
    // explícitas el caso queda todo en el mismo anillo exterior — ok.
    const distanceToDx = this.computeSemanticDistances(diagnoses);

    // Bucket por distancia: 1, 2, ∞
    type Bucket = { nodes: ClinicalNode[]; radiusMult: number };
    const buckets: Bucket[] = [
      { nodes: [], radiusMult: 0.85 }, // directos
      { nodes: [], radiusMult: 1.15 }, // segundo grado
      { nodes: [], radiusMult: 1.45 }, // periferia
    ];
    others.forEach(n => {
      const d = distanceToDx.get(n.data.id) ?? Infinity;
      const bucket = d <= 1 ? 0 : d === 2 ? 1 : 2;
      buckets[bucket].nodes.push(n);
    });

    // Ordenar dentro de cada bucket por sistema anatómico para mantener
    // clustering clínico cuando el anillo se recorre.
    buckets.forEach(bucket => {
      bucket.nodes.sort((a, b) => {
        const sa = a.data.system ?? 'zzz';
        const sb = b.data.system ?? 'zzz';
        if (sa !== sb) return sa.localeCompare(sb);
        return (a.data.type ?? '').localeCompare(b.data.type ?? '');
      });
    });

    // Repartir cada bucket en su propio anillo.
    buckets.forEach(bucket => {
      if (bucket.nodes.length === 0) return;
      const n = bucket.nodes.length;
      const angleStep = (Math.PI * 2) / n;
      const r = baseRadius * bucket.radiusMult;
      bucket.nodes.forEach((node, i) => {
        const angle = -Math.PI / 2 + i * angleStep;
        const jitter = Math.sin(i * 13.37 + bucket.radiusMult) * 0.3;
        node.position.set(
          center.x + (r + jitter) * Math.cos(angle),
          center.y + (r + jitter) * Math.sin(angle),
          center.z + Math.cos(i * 2.1) * 0.6
        );
        node.velocity.set(0, 0, 0);
      });
    });
  }

  /**
   * Calcula la distancia (en saltos de relationships) de cada nodo a
   * cualquier nodo de diagnóstico. Si un nodo no está conectado al dx,
   * queda sin entry → Infinity en el consumidor.
   */
  private computeSemanticDistances(diagnoses: ClinicalNode[]): Map<string, number> {
    const dist = new Map<string, number>();
    if (diagnoses.length === 0) return dist;

    // Índice bidireccional: por cada relationship A→B, lista de vecinos.
    const neighbors = new Map<string, Set<string>>();
    const addEdge = (a: string, b: string): void => {
      if (!neighbors.has(a)) neighbors.set(a, new Set());
      if (!neighbors.has(b)) neighbors.set(b, new Set());
      neighbors.get(a)!.add(b);
      neighbors.get(b)!.add(a);
    };
    this.nodes.forEach(n => {
      (n.data.relationships ?? []).forEach(rel => addEdge(n.data.id, rel.targetId));
    });

    // BFS multi-source desde los dx.
    const queue: string[] = [];
    diagnoses.forEach(d => {
      dist.set(d.data.id, 0);
      queue.push(d.data.id);
    });
    while (queue.length > 0) {
      const cur = queue.shift()!;
      const curDist = dist.get(cur)!;
      (neighbors.get(cur) ?? new Set()).forEach(next => {
        if (!dist.has(next)) {
          dist.set(next, curDist + 1);
          queue.push(next);
        }
      });
    }
    return dist;
  }

  private groupNodesByType(): Map<NodeType, ClinicalNode[]> {
    const groups = new Map<NodeType, ClinicalNode[]>();
    
    this.nodes.forEach(node => {
      const type = node.data.type as NodeType;
      if (!groups.has(type)) {
        groups.set(type, []);
      }
      groups.get(type)!.push(node);
    });
    
    return groups;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // ACTUALIZACIÓN
  // ═════════════════════════════════════════════════════════════════════════════
  
  update(deltaTime: number, globalTime: number): void {
    const hoveredId = state.interaction.hoveredNodeId;
    const selectedId = state.interaction.selectedNodeId;

    // Modo focus: al seleccionar un nodo, los que no están conectados a él
    // (por relationships, directas o inversas) bajan su opacidad para que
    // el alumno enfoque la atención. hover no activa focus — solo selected.
    const connectedIds = selectedId ? this.getConnectedIds(selectedId) : null;

    this.nodes.forEach(node => {
      node.update(deltaTime, globalTime);
      node.updateLabelState(node.data.id === hoveredId, node.data.id === selectedId);

      if (connectedIds) {
        const isFocus = connectedIds.has(node.data.id);
        const mat = node.mesh.material as THREE.MeshStandardMaterial;
        const targetOpacity = isFocus ? 1.0 : 0.28;
        mat.transparent = true;
        mat.opacity = THREE.MathUtils.lerp(mat.opacity, targetOpacity, 0.15);
        node.glowMaterial.opacity = isFocus
          ? node.renderState.glowIntensity
          : node.glowMaterial.opacity * 0.88;
      } else {
        // Sin foco: restaurar opacidad a 1 suavemente
        const mat = node.mesh.material as THREE.MeshStandardMaterial;
        mat.opacity = THREE.MathUtils.lerp(mat.opacity, 1.0, 0.12);
      }
    });

    // Actualizar líneas de conexión
    this.updateEdges();
  }

  /**
   * Devuelve el set de nodeIds directamente conectados al nodo dado
   * (el propio nodo + targets de sus relationships + nodos cuyas
   * relationships apuntan a él).
   */
  private getConnectedIds(nodeId: string): Set<string> {
    const result = new Set<string>([nodeId]);
    const node = this.nodes.get(nodeId);
    if (!node) return result;

    node.data.relationships.forEach(rel => result.add(rel.targetId));

    this.nodes.forEach(other => {
      other.data.relationships.forEach(rel => {
        if (rel.targetId === nodeId) result.add(other.data.id);
      });
    });

    return result;
  }

  private updateEdges(): void {
    if (!this.edgeLines || !state.ui.showEdges) return;

    const positions = this.edgeLines.geometry.attributes.position.array as Float32Array;
    const colors = this.edgeLines.geometry.attributes.color.array as Float32Array;
    const focusId = state.interaction.selectedNodeId ?? state.interaction.hoveredNodeId;

    let pIdx = 0;
    let cIdx = 0;
    this.edges.forEach(edge => {
      positions[pIdx++] = edge.source.position.x;
      positions[pIdx++] = edge.source.position.y;
      positions[pIdx++] = edge.source.position.z;
      positions[pIdx++] = edge.target.position.x;
      positions[pIdx++] = edge.target.position.y;
      positions[pIdx++] = edge.target.position.z;

      // Boost visual: si hay un nodo focused, las edges que NO lo tocan
      // se atenúan al 22% para que las relevantes resalten. Sin focus,
      // todas en intensidad plena.
      const baseColor = this.getRelationshipColor(edge.relationship.type);
      const touches = focusId
        ? edge.source.data.id === focusId || edge.target.data.id === focusId
        : true;
      const k = touches ? 1.0 : 0.22;
      colors[cIdx++] = baseColor.r * k;
      colors[cIdx++] = baseColor.g * k;
      colors[cIdx++] = baseColor.b * k;
      colors[cIdx++] = baseColor.r * k;
      colors[cIdx++] = baseColor.g * k;
      colors[cIdx++] = baseColor.b * k;
    });

    this.edgeLines.geometry.attributes.position.needsUpdate = true;
    this.edgeLines.geometry.attributes.color.needsUpdate = true;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // TOPOLOGÍAS
  // ═════════════════════════════════════════════════════════════════════════════
  
  applyTopology(mode: TopologyMode, _transitionDuration: number = 0.5): void {
    if (this.lastTopologyMode === mode) return;
    
    switch (mode) {
      case 'anatomical':
        this.applyAnatomicalLayout();
        break;
      case 'fisiopathological':
        this.applyFisiopathologicalLayout();
        break;
      case 'temporal_spiral':
        this.applyTemporalSpiralLayout();
        break;
      case 'differential_competitive':
        this.applyDifferentialCompetitiveLayout();
        break;
    }
    
    this.lastTopologyMode = mode;
  }

  private applyAnatomicalLayout(): void {
    // Agrupar por sistema anatómico
    const systemGroups = new Map<string, ClinicalNode[]>();
    
    this.nodes.forEach(node => {
      const system = node.data.system || 'other';
      if (!systemGroups.has(system)) {
        systemGroups.set(system, []);
      }
      systemGroups.get(system)!.push(node);
    });
    
    // Posicionar sistemas en anillo alrededor del centro
    const systems = Array.from(systemGroups.keys());
    const angleStep = (2 * Math.PI) / Math.max(systems.length, 1);
    
    systems.forEach((system, systemIndex) => {
      const nodes = systemGroups.get(system)!;
      const centerAngle = systemIndex * angleStep;
      const centerRadius = 3;
      
      const centerX = Math.cos(centerAngle) * centerRadius;
      const centerZ = Math.sin(centerAngle) * centerRadius;
      
      nodes.forEach((node, nodeIndex) => {
        const nodeAngle = nodeIndex * 0.5;
        const nodeRadius = 1;
        
        node.targetPosition = new THREE.Vector3(
          centerX + Math.cos(nodeAngle) * nodeRadius,
          (nodeIndex - nodes.length / 2) * 0.5,
          centerZ + Math.sin(nodeAngle) * nodeRadius
        );
      });
    });
  }

  private applyFisiopathologicalLayout(): void {
    // Layout de cascada causal
    // Nodos ordenados por tiempo de aparición y causalidad
    
    const sortedNodes = Array.from(this.nodes.values()).sort((a, b) => {
      const timeA = new Date(a.data.onsetDateTime).getTime();
      const timeB = new Date(b.data.onsetDateTime).getTime();
      return timeA - timeB;
    });
    
    sortedNodes.forEach((node, index) => {
      const x = (index % 5) * 1.5 - 3;
      const y = Math.floor(index / 5) * -1.5 + 2;
      const z = Math.sin(index * 0.5) * 2;
      
      node.targetPosition = new THREE.Vector3(x, y, z);
    });
  }

  private applyTemporalSpiralLayout(): void {
    // Espiral 3D con tiempo en eje Z
    const now = Date.now();
    
    this.nodes.forEach(node => {
      const nodeTime = new Date(node.data.onsetDateTime).getTime();
      const hoursDiff = (now - nodeTime) / (1000 * 60 * 60);
      
      const spiralRadius = 3;
      const turns = 2;
      const height = Math.max(-5, Math.min(5, hoursDiff * 0.1));
      const angle = (hoursDiff / 24) * turns * 2 * Math.PI;
      
      node.targetPosition = new THREE.Vector3(
        Math.cos(angle) * spiralRadius,
        height,
        Math.sin(angle) * spiralRadius
      );
    });
  }

  private applyDifferentialCompetitiveLayout(): void {
    // Múltiples centros gravitatorios para diagnósticos
    const diagnosisNodes = Array.from(this.nodes.values()).filter(
      n => n.data.type === 'diagnosis'
    );
    
    const symptomNodes = Array.from(this.nodes.values()).filter(
      n => n.data.type === 'symptom' || n.data.type === 'vital_sign' || n.data.type === 'laboratory'
    );
    
    // Posicionar diagnósticos en círculo
    const diagCount = diagnosisNodes.length;
    const angleStep = (2 * Math.PI) / Math.max(diagCount, 1);
    
    diagnosisNodes.forEach((node, index) => {
      const angle = index * angleStep;
      const radius = 4;
      
      node.targetPosition = new THREE.Vector3(
        Math.cos(angle) * radius,
        0,
        Math.sin(angle) * radius
      );
    });
    
    // Posicionar síntomas según qué diagnóstico explican mejor
    symptomNodes.forEach(node => {
      // Buscar el diagnóstico más relacionado
      let bestDiagnosis = diagnosisNodes[0];
      let bestStrength = 0;
      
      node.data.relationships.forEach(rel => {
        const targetNode = this.nodes.get(rel.targetId);
        if (targetNode && targetNode.data.type === 'diagnosis') {
          if (rel.strength > bestStrength) {
            bestStrength = rel.strength;
            bestDiagnosis = targetNode;
          }
        }
      });
      
      // Posicionar cerca del mejor diagnóstico
      if (bestDiagnosis && bestDiagnosis.targetPosition) {
        node.targetPosition = bestDiagnosis.targetPosition.clone().add(
          new THREE.Vector3(
            (Math.random() - 0.5) * 2,
            (Math.random() - 0.5) * 2,
            (Math.random() - 0.5) * 2
          )
        );
      }
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // ACCESO A NODOS
  // ═════════════════════════════════════════════════════════════════════════════
  
  getNode(id: string): ClinicalNode | undefined {
    return this.nodes.get(id);
  }

  getNodesByType(type: string): ClinicalNode[] {
    return Array.from(this.nodes.values()).filter(n => n.data.type === type);
  }

  getNodesBySystem(system: AnatomicalSystem): ClinicalNode[] {
    return Array.from(this.nodes.values()).filter(n => n.data.system === system);
  }

  getCriticalNodes(): ClinicalNode[] {
    return Array.from(this.nodes.values()).filter(n => n.isCritical);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // EMERGENCIA ABC
  // ═════════════════════════════════════════════════════════════════════════════
  
  filterABCNodes(): void {
    // Mostrar solo Airway, Breathing, Circulation (nodos críticos)
    const criticalTypes = ['vital_sign', 'symptom'];
    
    this.nodes.forEach(node => {
      const isCritical = node.isCritical;
      const isRelevantType = criticalTypes.includes(node.data.type);
      
      node.visible = isCritical || isRelevantType;
      node.mesh.visible = isCritical || isRelevantType;
      node.glow.visible = isCritical || isRelevantType;
      if (node.label) {
        node.label.visible = isCritical && state.ui.showLabels;
      }
    });
  }

  showAllNodes(): void {
    this.nodes.forEach(node => {
      node.visible = true;
      node.mesh.visible = true;
      node.glow.visible = true;
      if (node.label) {
        node.label.visible = state.ui.showLabels;
      }
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // HIDE / REVEAL (para modo quiz)
  // ═════════════════════════════════════════════════════════════════════════════

  /**
   * Discovery mode: oculta labs, imágenes y diagnóstico al cargar el caso.
   * El alumno arranca viendo sólo anamnesis + síntomas + vitales iniciales,
   * y tiene que ordenar exámenes o razonar el dx para revelar el resto.
   * Esto transforma el grafo estático en un ejercicio progresivo.
   */
  applyDiscoveryMode(): void {
    this.nodes.forEach(node => {
      const t = node.data.type;
      const hide = t === 'laboratory' || t === 'imaging' || t === 'diagnosis';
      if (hide) {
        node.visible = false;
        node.mesh.visible = false;
        node.glow.visible = false;
        if (node.label) node.label.visible = false;
      }
    });
  }

  /** Al revelar un finding (examen ordenado, gesto clínico, timeline). */
  revealByFindingId(nodeId: string): void {
    const node = this.nodes.get(nodeId);
    if (node && !node.visible) {
      node.visible = true;
      node.mesh.visible = true;
      node.glow.visible = true;
      if (node.label) node.label.visible = state.ui.showLabels;
      node.pulse();
    }
  }

  hideNodesByType(type: string): string[] {
    const hiddenIds: string[] = [];
    this.nodes.forEach(node => {
      if (node.data.type === type) {
        node.visible = false;
        node.mesh.visible = false;
        node.glow.visible = false;
        if (node.label) node.label.visible = false;
        hiddenIds.push(node.data.id);
      }
    });
    return hiddenIds;
  }

  revealNode(nodeId: string): ClinicalNode | undefined {
    const node = this.nodes.get(nodeId);
    if (node) {
      node.visible = true;
      node.mesh.visible = true;
      node.glow.visible = true;
      if (node.label) node.label.visible = state.ui.showLabels;
      node.explode();
      node.pulse();
    }
    return node;
  }

  takeSnapshot(): { nodes: ClinicalNodeData[]; triagePriority: TriagePriority } {
    return {
      nodes: this.data.nodes.map(n => ({ ...n })),
      triagePriority: this.data.triagePriority,
    };
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // MODIFICACIÓN DE DATOS
  // ═════════════════════════════════════════════════════════════════════════════
  
  updateNodeValue(nodeId: string, newValue: unknown): void {
    const node = this.nodes.get(nodeId);
    if (!node) return;
    
    if (typeof newValue === 'number' && node.data.valueQuantity) {
      node.data.valueQuantity.value = newValue;
    } else if (typeof newValue === 'string') {
      node.data.valueString = newValue;
    } else if (typeof newValue === 'boolean') {
      node.data.valueBoolean = newValue;
    }
    
    node.updateLabel();
    node.checkCriticalStatus();
  }

  addNode(nodeData: ClinicalNodeData): void {
    const node = new ClinicalNode(nodeData);
    this.nodes.set(nodeData.id, node);
    this.add(node);
    this.data.nodes.push(nodeData);
    
    // Recrear conexiones
    this.createEdges();
  }

  removeNode(nodeId: string): void {
    const node = this.nodes.get(nodeId);
    if (!node) return;
    
    node.dispose();
    this.remove(node);
    this.nodes.delete(nodeId);
    
    // Remover de datos
    const index = this.data.nodes.findIndex(n => n.id === nodeId);
    if (index !== -1) {
      this.data.nodes.splice(index, 1);
    }
    
    // Recrear conexiones
    this.createEdges();
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // LIMPIEZA
  // ═════════════════════════════════════════════════════════════════════════════
  
  dispose(): void {
    this.nodes.forEach(node => node.dispose());
    this.nodes.clear();
    
    if (this.edgeLines) {
      this.edgeLines.geometry.dispose();
      (this.edgeLines.material as THREE.Material).dispose();
    }
    
    this.edges = [];
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// TIPOS AUXILIARES
// ═══════════════════════════════════════════════════════════════════════════════

interface EdgeConnection {
  source: ClinicalNode;
  target: ClinicalNode;
  relationship: ClinicalRelationship;
  bidirectional: boolean;
}

// Extender ClinicalNode para agregar targetPosition
declare module './ClinicalNode' {
  interface ClinicalNode {
    targetPosition?: THREE.Vector3;
  }
}

export default Patient;
